import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Card,
  CardContent,
  Link,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import listIcon from '../../assets/icons/list.svg';
import piledFoundationIcon from '../../assets/icons/piled_foundation.svg';
import compositeColumnIcon from '../../assets/icons/composite_column.svg';
import slabCalculatorIcon from '../../assets/icons/slab_calculator.svg';
import boltAssemblyIcon from '../../assets/icons/bolt-assembly.svg';
import trussFrameIcon from '../../assets/icons/Truss_Frame.svg';
import CalculateIcon from '@mui/icons-material/Calculate';
import EngineeringIcon from '@mui/icons-material/Engineering';
import MapIcon from '@mui/icons-material/Map';
import axios from 'axios';
import { useWorkspace } from '../../context/WorkspaceContext';

const APP_LIST_ICON_SIZE = 48;
const appListItemIconSx = { minWidth: 72 };
const appListMuiIconSx = { fontSize: APP_LIST_ICON_SIZE };

const NavIcon = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    width={APP_LIST_ICON_SIZE}
    height={APP_LIST_ICON_SIZE}
    style={{ display: 'block' }}
  />
);

const APP_ROUTE = {
  beam_formulae: '/formulae',
  foundations: '/foundations',
  composite_columns: '/composite-columns',
  console: '/console',
  slab: '/slab_calculator',
  truss: '/truss',
  site_planner: '/site-planner',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { isLoggedIn, openLogin, workspace } = useWorkspace();
  const [calcTab, setCalcTab] = useState(0);
  const [recentItems, setRecentItems] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [contactFormUrlFromServer, setContactFormUrlFromServer] = useState('');

  useEffect(() => {
    axios
      .get('/api/public-config/')
      .then((r) => setContactFormUrlFromServer((r.data && r.data.contact_form_url) || ''))
      .catch(() => setContactFormUrlFromServer(''));
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setRecentItems([]);
      setSavedItems([]);
      return;
    }
    axios
      .get('/api/recent-calculations/')
      .then((r) => setRecentItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRecentItems([]));
    axios
      .get('/api/saved-calculations/')
      .then((r) => setSavedItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSavedItems([]));
  }, [isLoggedIn, workspace?.workspaceId]);

  const handleAppClick = (appPath) => {
    navigate(appPath);
  };

  const handleRecentClick = (item) => {
    const base = APP_ROUTE[item.app];
    if (!base || !item.session_id) return;
    navigate(`${base}?calcSession=${encodeURIComponent(item.session_id)}`);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatSummary = (item) => {
    const s = item.summary;
    if (!s || typeof s !== 'object') return '';
    if (item.app === 'beam_formulae') {
      return [s.cross_section_type, s.cross_section_name].filter(Boolean).join(' ') || '—';
    }
    if (item.app === 'foundations') {
      const u = s.utilization_summary;
      const util =
        u && u.max_utilization != null
          ? `Max util: ${(Number(u.max_utilization) * 100).toFixed(1)}%`
          : '';
      return [s.foundation_type, s.pile_type, util].filter(Boolean).join(' · ');
    }
    if (item.app === 'composite_columns') {
      return [s.comcol_section_name, s.comcol_column_type].filter(Boolean).join(' · ');
    }
    if (item.app === 'console') {
      return `V=${s.vertical_load_kn ?? '—'} kN, H=${s.horizontal_load_kn ?? '—'} kN`;
    }
    if (item.app === 'slab') {
      if (s.L_m != null && s.W_m != null) return `${s.L_m}×${s.W_m} m${s.slab_type ? ` · ${s.slab_type}` : ''}`;
    }
    if (item.app === 'truss') {
      const parts = [];
      if (s.span_m != null) parts.push(`Span ${s.span_m} m`);
      if (s.chord_family) parts.push(s.chord_family);
      return parts.join(' · ') || '—';
    }
    if (item.app === 'site_planner') {
      const parts = [];
      if (s.mode) parts.push(s.mode.toUpperCase());
      if (s.module1_count != null) parts.push(`${s.module1_count} modules`);
      if (s.usable_area_m2 != null) parts.push(`${Math.round(s.usable_area_m2)} m²`);
      return parts.join(' · ') || '—';
    }
    return '';
  };

  const appLabel = (app) => {
    switch (app) {
      case 'beam_formulae':
        return 'Beam calculator';
      case 'foundations':
        return 'Foundation';
      case 'composite_columns':
        return 'Composite column';
      case 'console':
        return 'Console';
      case 'slab':
        return 'Slab';
      case 'truss':
        return 'Truss';
      case 'site_planner':
        return 'Site planner';
      default:
        return app || 'Calculation';
    }
  };

  const year = new Date().getFullYear();
  const contactHref =
    (process.env.REACT_APP_CONTACT_FORM_URL || '').trim() || (contactFormUrlFromServer || '').trim();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Structural Engineering Dashboard
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: '100%',
              minHeight: '70vh',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Applications
            </Typography>

            <List component="nav">
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/cross-sections')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <NavIcon src={listIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Cross Sections"
                    secondary="Database of structural section properties"
                  />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/bolts')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <NavIcon src={boltAssemblyIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Bolt tables"
                    secondary="Metric bolt capacities, ISO 4032 nuts, washers (EN 1993-1-8 reference)"
                  />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/formulae')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <CalculateIcon sx={appListMuiIconSx} />
                  </ListItemIcon>
                  <ListItemText primary="Beam Calculator" secondary="Calculate beam deflections and forces" />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/foundations')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <NavIcon src={piledFoundationIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Foundation Analysis"
                    secondary="EN 1997 Foundation calculation tool"
                  />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/composite-columns')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <NavIcon src={compositeColumnIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Composite Columns"
                    secondary="Composite column design (EN 1994)"
                  />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/console')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <EngineeringIcon sx={appListMuiIconSx} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Console Calculations"
                    secondary="Concrete corbel and neoprene pad calculation tool"
                  />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/slab_calculator')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <NavIcon src={slabCalculatorIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Slab Analysis"
                    secondary="Precast and hollow core slab calculations"
                  />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/truss')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <NavIcon src={trussFrameIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Truss & frame (steel)"
                    secondary="Pin-jointed truss / portal frame, EC3 member and joint checks"
                  />
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              <ListItem disablePadding>
                <ListItemButton onClick={() => handleAppClick('/site-planner')}>
                  <ListItemIcon sx={appListItemIconSx}>
                    <MapIcon sx={appListMuiIconSx} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Site Planner"
                    secondary="Setbacks, DXF tracing, and modular layout optimizer"
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: '100%',
              minHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Calculations
            </Typography>
            <Tabs
              value={calcTab}
              onChange={(_, v) => setCalcTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Recent" />
              <Tab label="Saved" />
            </Tabs>

            {calcTab === 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Auto-saved after you run an analysis (from any calculator).
                </Typography>
                {!isLoggedIn ? (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    <Button variant="outlined" size="small" onClick={openLogin} sx={{ mt: 1 }}>
                      Sign in
                    </Button>
                    {' '}
                    with a username to see your recent calculations here.
                  </Typography>
                ) : recentItems.length === 0 ? (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    No recent calculations yet. Run an analysis in any app to see it here.
                  </Typography>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    {recentItems.map((item) => (
                      <Card
                        key={`recent-${item.app}-${item.session_id}`}
                        sx={{
                          mb: 2,
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                            cursor: 'pointer',
                          },
                        }}
                        onClick={() => handleRecentClick(item)}
                      >
                        <CardContent>
                          <Typography variant="overline" color="primary">
                            {appLabel(item.app)}
                          </Typography>
                          <Typography variant="h6">{item.name || 'Untitled'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(item.created_at)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {formatSummary(item) || '—'}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </>
            )}

            {calcTab === 1 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Calculations you pinned or saved with a name (History → Save).
                </Typography>
                {!isLoggedIn ? (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    <Button variant="outlined" size="small" onClick={openLogin} sx={{ mt: 1 }}>
                      Sign in
                    </Button>
                    {' '}
                    to access saved calculations from any device.
                  </Typography>
                ) : savedItems.length === 0 ? (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    No saved calculations yet. Use the Save button or pin a recent item from a calculator’s History
                    drawer.
                  </Typography>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    {savedItems.map((item) => (
                      <Card
                        key={`saved-${item.app}-${item.session_id}`}
                        sx={{
                          mb: 2,
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                            cursor: 'pointer',
                          },
                        }}
                        onClick={() => handleRecentClick(item)}
                      >
                        <CardContent>
                          <Typography variant="overline" color="primary">
                            {appLabel(item.app)}
                          </Typography>
                          <Typography variant="h6">{item.name || 'Untitled'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(item.created_at)}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {formatSummary(item) || '—'}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Box
        component="footer"
        sx={{
          mt: 4,
          pt: 3,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: contactHref || process.env.NODE_ENV === 'development' ? 1 : 0 }}>
          © {year} Structural Engineering App. All rights reserved.
        </Typography>
        {contactHref ? (
          <Link
            href={contactHref}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            variant="body2"
          >
            Contact form
          </Link>
        ) : process.env.NODE_ENV === 'development' ? (
          <Typography variant="caption" color="text.disabled" display="block">
            Contact link hidden: set <code>CONTACT_FORM_URL</code> for Django or{' '}
            <code>REACT_APP_CONTACT_FORM_URL</code> in frontend <code>.env</code>, then restart servers.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default Dashboard;
